import React, { memo } from 'react';
import classnames from 'classnames';
import { FiAlertTriangle, FiCheck, FiTrash2, FiX } from 'react-icons/fi';

import { PianoRollSvgLayer } from './PianoRollSvgLayer';

const svgSize = 16;

const PianoRollNotes = memo(
   ({
      pattern,
      referencePattern,
      compareAnswer,
      lowNote,
      highNote,
      noteHeight,
      beatCount,
      partsPerBeat,
      partWidth,
      onMouseDown,
      onNoteMousedown,
      onNoteMouseenter,
      onNoteResizeHandleMousedown,
      clickNoteDeleteIcon,
      cachedNoteSettings,
      touchEnabled,
   }) => {
      const gridHeight = (highNote - lowNote + 1) * noteHeight;
      const gridWidth = beatCount * partsPerBeat * partWidth;

      return (
         <PianoRollSvgLayer
            width={gridWidth}
            height={gridHeight}
            onMouseDown={(e) => {
               const elementBounds = e.currentTarget.getBoundingClientRect();
               if (e.target === e.currentTarget) {
                  onMouseDown(
                     e.button,
                     e.clientX - elementBounds.left,
                     e.clientY - elementBounds.top,
                     e.clientX,
                     e.clientY
                  );
               }
            }}
            onContextMenu={(e) => e.preventDefault()}
         >
            {compareAnswer &&
               referencePattern &&
               referencePattern
                  .flat()
                  .map(({ note, startPart, noteLength }) => {
                     const x = startPart * partWidth;
                     const y = (note - highNote) * -1 * noteHeight;
                     return (
                        !(
                           pattern[startPart].find(
                              (referenceNote) => referenceNote.note === note
                           ) &&
                           pattern[startPart].find(
                              (referenceNote) => referenceNote.note === note
                           ).noteLength === noteLength
                        ) && (
                           <g
                              key={`ref-${startPart}-${note}`}
                              className="reference-note"
                           >
                              {noteLength == null ? (
                                 <polygon
                                    className="sustained-note"
                                    points={`${x},${y} ${
                                       x + (partsPerBeat / 4) * partWidth
                                    },${y + noteHeight / 2} ${x},${
                                       y + noteHeight
                                    }`}
                                 />
                              ) : (
                                 <rect
                                    x={startPart * partWidth}
                                    y={(note - highNote) * -1 * noteHeight}
                                    width={partWidth * noteLength}
                                    height={noteHeight}
                                 />
                              )}
                              <FiAlertTriangle
                                 size={svgSize}
                                 x={startPart * partWidth + 2}
                                 y={
                                    (note - highNote) * -1 * noteHeight +
                                    (noteHeight - svgSize) / 2
                                 }
                              />
                           </g>
                        )
                     );
                  })}
            {pattern.flat().map(({ note, startPart, noteLength }) => {
               const matchesReference = referencePattern?.[startPart].find(
                  (referenceNote) => referenceNote.note === note
               );

               const incorrectLength =
                  referencePattern?.[startPart].find(
                     (referenceNote) => referenceNote.note === note
                  ) &&
                  referencePattern?.[startPart].find(
                     (referenceNote) => referenceNote.note === note
                  ).noteLength !== noteLength;

               const noteX = startPart * partWidth;
               const noteY = (note - highNote) * -1 * noteHeight;

               return (
                  <g
                     key={`${startPart}-${note}`}
                     onMouseEnter={() => onNoteMouseenter(startPart, note)}
                     className={classnames({
                        'user-note': true,
                        reference: matchesReference,
                        'incorrect-length': incorrectLength,
                     })}
                  >
                     {noteLength == null ? (
                        <polygon
                           className="sustained-note"
                           points={`${noteX},${noteY} ${
                              noteX + (partsPerBeat / 4) * partWidth
                           },${noteY + noteHeight / 2} ${noteX},${
                              noteY + noteHeight
                           }`}
                           onMouseDown={(e) =>
                              onNoteMousedown(
                                 startPart,
                                 note,
                                 e.button,
                                 e.clientX,
                                 e.clientY
                              )
                           }
                           data-note={note}
                           data-start-part={startPart}
                           data-note-body
                        />
                     ) : (
                        <rect
                           x={startPart * partWidth}
                           y={(note - highNote) * -1 * noteHeight}
                           width={partWidth * noteLength}
                           height={noteHeight}
                           onMouseDown={(e) =>
                              onNoteMousedown(
                                 startPart,
                                 note,
                                 e.button,
                                 e.clientX,
                                 e.clientY
                              )
                           }
                           data-note={note}
                           data-note-length={noteLength}
                           data-start-part={startPart}
                           data-note-body
                        />
                     )}
                     {!compareAnswer && noteLength != null && (
                        <rect
                           className="note-resize-handle"
                           x={
                              startPart * partWidth +
                              partWidth * noteLength -
                              Math.min((partWidth * noteLength) / 2, 7) +
                              2
                           }
                           y={(note - highNote) * -1 * noteHeight}
                           width={Math.min((partWidth * noteLength) / 2, 7)}
                           height={noteHeight}
                           onMouseDown={(e) =>
                              onNoteResizeHandleMousedown(
                                 startPart,
                                 note,
                                 noteLength,
                                 e.button,
                                 e.clientX,
                                 e.clientY
                              )
                           }
                        />
                     )}
                     {compareAnswer && matchesReference && !incorrectLength && (
                        <FiCheck
                           size={svgSize}
                           x={startPart * partWidth + 2}
                           y={
                              (note - highNote) * -1 * noteHeight +
                              (noteHeight - svgSize) / 2
                           }
                        />
                     )}
                     {compareAnswer &&
                        (!matchesReference || incorrectLength) && (
                           <FiX
                              size={svgSize}
                              x={startPart * partWidth + 2}
                              y={
                                 (note - highNote) * -1 * noteHeight +
                                 (noteHeight - svgSize) / 2
                              }
                           />
                        )}
                  </g>
               );
            })}

            {touchEnabled &&
               pattern
                  .flat()
                  .filter(
                     (note) =>
                        !compareAnswer &&
                        note.startPart === cachedNoteSettings.startPart &&
                        // note.noteLength === cachedNoteSettings.noteLength &&
                        note.note === cachedNoteSettings.note
                  )
                  .map(({ startPart, noteLength, note }) => (
                     <g key={`${startPart}-${note}`}>
                        {noteLength != null && (
                           <circle
                              className="note-touch-resize-handle"
                              cx={
                                 startPart * partWidth +
                                 partWidth * noteLength +
                                 noteHeight / 2
                              }
                              cy={
                                 (note - highNote) * -1 * noteHeight +
                                 noteHeight / 2
                              }
                              r={noteHeight / 2 - 5}
                              onMouseDown={(e) =>
                                 onNoteResizeHandleMousedown(
                                    startPart,
                                    note,
                                    noteLength,
                                    e.button,
                                    e.clientX,
                                    e.clientY
                                 )
                              }
                              data-note={note}
                              data-note-length={noteLength}
                              data-start-part={startPart}
                              data-note-resize
                           />
                        )}
                        <FiTrash2
                           className="note-touch-delete-icon"
                           size={noteHeight - 10}
                           x={startPart * partWidth + 2}
                           y={
                              (note - highNote) * -1 * noteHeight -
                              noteHeight +
                              3
                           }
                        />
                        <rect
                           className="note-touch-delete-rect"
                           width={noteHeight}
                           height={noteHeight}
                           x={startPart * partWidth}
                           y={(note - highNote) * -1 * noteHeight - noteHeight}
                           onClick={() => clickNoteDeleteIcon(note, startPart)}
                        />
                     </g>
                  ))}
         </PianoRollSvgLayer>
      );
   }
);

export { PianoRollNotes };
